import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMultiFetch } from '../hooks/useMultiFetch';
import StatusBanner from '../components/ui/StatusBanner';
import Button from '../components/ui/Button';
import { StatusDot, StatusPill } from '../components/ui/Status';
import KpiCard from '../components/ui/KpiCard';
import WorkspacePage from '../components/layout/WorkspacePage';
import EmptyState from '../components/ui/EmptyState';
import SkeletonBlock from '../components/ui/SkeletonBlock';
import { withQuery } from '../utils/buildQuery';
import { deriveDateRangeForPeriod } from '../utils/periodRange';
import { formatMetricValue, formatPercent } from '../utils/formatMetric';
import { getScopeRollupLabel, userHasPermission } from '../config/appRoutes';
import { getFocusQueueCounts } from '../services/focusQueueApi';
import {
  applyFocusQueueCountDelta,
  subscribeToFocusQueueChanges
} from '../utils/focusQueueEvents';
import {
  COACHING_UPDATED_EVENT,
  DIRECTOR_DATA_UPDATED_EVENT
} from '../utils/directorEvents';
import './ExecutiveDashboard.css';

const ALL_LOCATIONS_CODE = 'ALL';

/**
 * Executive Snapshot — the premium leadership landing page.
 * Reads existing data models only:
 *   - /api/tracker/location   (pre-aggregated KPI scorecard)
 *   - /api/approvals/counts   (approval queue pressure)
 *   - /api/events/attendance/summary (atWorkRate)
 *   - /api/events/attendance/risk (at-risk managers, if permitted)
 *
 * Designed for 30-second clarity: pulse → scorecard → attention list.
 */
function ExecutiveDashboard({
  user,
  selectedLocationCode = ALL_LOCATIONS_CODE,
  selectedPeriod = 'monthly',
  locationLabel = '',
  onNavigate
}) {
  const locationScope = selectedLocationCode && selectedLocationCode !== ALL_LOCATIONS_CODE
    ? selectedLocationCode
    : null;
  const canSeeRisk = userHasPermission(user, 'view_all_locations');
  const dateRange = useMemo(() => deriveDateRangeForPeriod(selectedPeriod), [selectedPeriod]);

  const urlMap = useMemo(() => ({
    tracker: withQuery('/api/tracker/location', {
      locationCode: locationScope || undefined,
      periodType: selectedPeriod
    }),
    approvals: '/api/approvals/counts',
    attendanceSummary: withQuery('/api/events/attendance/summary', {
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      locationCode: locationScope || undefined
    }),
    risk: canSeeRisk
      ? withQuery('/api/events/attendance/risk', {
        windowDays: 30,
        locationCode: locationScope || undefined
      })
      : null,
    alerts: withQuery('/api/alerts', {
      locationCode: locationScope || undefined,
      period: selectedPeriod
    })
  }), [locationScope, selectedPeriod, dateRange.startDate, dateRange.endDate, canSeeRisk]);

  const { data, isLoading, error, refetch } = useMultiFetch(urlMap, {
    deps: [locationScope, selectedPeriod, canSeeRisk]
  });

  useEffect(() => {
    const handleDirectorDataUpdated = () => refetch();
    window.addEventListener(DIRECTOR_DATA_UPDATED_EVENT, handleDirectorDataUpdated);
    return () => window.removeEventListener(DIRECTOR_DATA_UPDATED_EVENT, handleDirectorDataUpdated);
  }, [refetch]);

  const tracker = data.tracker || null;
  const approvals = data.approvals || null;
  const attendance = data.attendanceSummary || null;
  const risk = useMemo(
    () => (Array.isArray(data.risk) ? data.risk : []),
    [data.risk]
  );

  const metrics = useMemo(
    () => (Array.isArray(tracker?.metrics) ? tracker.metrics : []),
    [tracker]
  );
  const summary = tracker?.summary || null;
  const topMetrics = useMemo(() => prioritizeMetrics(metrics).slice(0, 6), [metrics]);

  // Alerts payload is the canonical "what needs attention" source — served by
  // /api/alerts so the Alerts Center, the exec page, and the Monday digest
  // never drift apart. Preview only the top few here; the chip links to the
  // full list.
  const alertsPayload = data.alerts || null;
  const allAlertActions = useMemo(
    () => (Array.isArray(alertsPayload?.actions) ? alertsPayload.actions : []),
    [alertsPayload]
  );
  const totalAlertCount = allAlertActions.length
    + (alertsPayload?.counts?.truncated || 0);
  const hasCriticalAlert = useMemo(
    () => allAlertActions.some(a => a.severity === 'critical'),
    [allAlertActions]
  );

  // ----- v2: Top Priorities & Recommended Actions -----
  // Both lists ride existing payloads — no new fetches, no new endpoints.
  // Priorities surface the highest-severity alerts the operator needs to
  // look at; Actions translate the broader signal mix (queue depth, at-
  // risk people, recognition opportunities, coverage gaps) into concrete
  // "do this next" items, each with a real route to drill into.

  // Priorities: top 3 of (critical → warn → info), then empty-state.
  const topPriorities = useMemo(() => {
    if (!Array.isArray(allAlertActions) || !allAlertActions.length) return [];
    const bySeverity = (s) => allAlertActions.filter(a => a.severity === s);
    const ranked = [
      ...bySeverity('critical'),
      ...bySeverity('warn'),
      ...bySeverity('info')
    ];
    return ranked.slice(0, 3);
  }, [allAlertActions]);

  const [focusQueueCounts, setFocusQueueCounts] = useState({ total: 0, overdue: 0, byType: {} });
  const loadFocusQueueCounts = useCallback(() => {
    let mounted = true;
    getFocusQueueCounts()
      .then((nextCounts = {}) => {
        if (!mounted) return;
        setFocusQueueCounts({
          total: Number(nextCounts.total || 0),
          overdue: Number(nextCounts.overdue || 0),
          byType: nextCounts.byType && typeof nextCounts.byType === 'object'
            ? nextCounts.byType
            : {}
        });
      })
      .catch(() => { /* focus queue is non-critical for the snapshot */ });
    return () => { mounted = false; };
  }, []);

  useEffect(() => loadFocusQueueCounts(), [loadFocusQueueCounts]);

  useEffect(() => subscribeToFocusQueueChanges((event) => {
    const { action, item } = event.detail || {};
    if ((action === 'dismiss' || action === 'restore') && item?.itemKey) {
      setFocusQueueCounts(current => applyFocusQueueCountDelta(
        current,
        item,
        action === 'dismiss' ? -1 : 1
      ));
      return;
    }
    if (action === 'refresh') {
      loadFocusQueueCounts();
    }
  }), [loadFocusQueueCounts]);

  useEffect(() => {
    const handleDirectorDataUpdated = () => loadFocusQueueCounts();
    const handleCoachingUpdated = () => loadFocusQueueCounts();
    window.addEventListener(DIRECTOR_DATA_UPDATED_EVENT, handleDirectorDataUpdated);
    window.addEventListener(COACHING_UPDATED_EVENT, handleCoachingUpdated);
    return () => {
      window.removeEventListener(DIRECTOR_DATA_UPDATED_EVENT, handleDirectorDataUpdated);
      window.removeEventListener(COACHING_UPDATED_EVENT, handleCoachingUpdated);
    };
  }, [loadFocusQueueCounts]);

  // Recommended actions: rules-based, deterministic, no LLM. Order
  // mirrors operator priorities: queue → overdue focus items → people →
  // recognition → hygiene.
  const recommendedActions = useMemo(() => {
    const out = [];
    const pending = approvals?.pendingForReview ?? 0;
    const returned = approvals?.returnedToMe ?? 0;

    if (pending > 0 || returned > 0) {
      out.push({
        id: 'action-approvals',
        title: pending > 0
          ? `Review ${pending} pending request${pending === 1 ? '' : 's'} in your queue`
          : `${returned} request${returned === 1 ? '' : 's'} returned to you`,
        subtitle: returned > 0
          ? `${returned} returned to you · awaiting decision`
          : 'Awaiting decision — open the queue to triage.',
        cta: { label: 'Open queue', path: '/approvals' },
        tone: returned > 0 ? 'behind' : 'watch'
      });
    }

    // Focus queue overdue rule — broadens coaching follow-ups to every
    // source in the user's derived queue.
    if (focusQueueCounts.overdue > 0) {
      out.push({
        id: 'action-focus-queue',
        title: `Review ${focusQueueCounts.overdue} overdue focus item${focusQueueCounts.overdue === 1 ? '' : 's'}`,
        subtitle: `${focusQueueCounts.total || focusQueueCounts.overdue} total in your queue.`,
        cta: { label: 'Open Today', path: '/today' },
        tone: 'behind'
      });
    }

    if (Array.isArray(risk) && risk.length > 0) {
      const sample = risk.slice(0, 3).map(r => r.managerName || `Manager #${r.managerId}`).filter(Boolean);
      const tail = risk.length > sample.length ? '…' : '';
      out.push({
        id: 'action-attendance',
        title: `Reach out to ${risk.length} manager${risk.length === 1 ? '' : 's'} trending below at-work threshold`,
        subtitle: sample.length ? `${sample.join(', ')}${tail}` : 'See attendance for details',
        cta: { label: 'Open attendance', path: '/manager-performance' },
        tone: 'behind'
      });
    }

    const winsList = Array.isArray(alertsPayload?.wins) ? alertsPayload.wins : [];
    if (winsList.length > 0 && out.length < 3) {
      const top = winsList[0];
      // Strip the "X is on a streak — " prefix the alert generator adds so
      // we surface just the location/KPI for recognition copy.
      const focus = String(top?.title || '').replace(/\s*is on a streak.*$/, '').trim();
      out.push({
        id: 'action-recognize',
        title: focus
          ? `Recognize ${focus} — on a streak`
          : `Recognize ${winsList.length} streak win${winsList.length === 1 ? '' : 's'}`,
        subtitle: `${winsList.length} streak${winsList.length === 1 ? '' : 's'} this period · worth a manager-review note`,
        cta: { label: 'Open scorecard', path: '/location-scorecard' },
        tone: 'good'
      });
    }

    // Coverage hygiene — only if we still have room AND the period is
    // materially elapsed (no false alarms two days into the month).
    const cov = Number(summary?.coverageRatio);
    const sampleElapsed = Array.isArray(metrics)
      ? metrics.find(m => Number.isFinite(m?.elapsedPercent))?.elapsedPercent
      : null;
    if (out.length < 3
        && Number.isFinite(cov) && cov < 0.8
        && Number.isFinite(sampleElapsed) && sampleElapsed > 50) {
      out.push({
        id: 'action-coverage',
        title: 'Chase missing actuals from locations not yet reporting',
        subtitle: `${Math.round(cov * 100)}% of KPIs have actuals for this period`,
        cta: { label: 'Open actuals', path: '/actuals' },
        tone: 'watch'
      });
    }

    // Always-on default so the panel never reads empty for a healthy
    // tenant: prompt to set the next period's targets.
    if (out.length === 0) {
      out.push({
        id: 'action-plan-next',
        title: 'Set goals for the next period',
        subtitle: 'Lock in next-month targets while everything is on track.',
        cta: { label: 'Open goals', path: '/goals' },
        tone: 'good'
      });
    }

    return out.slice(0, 3);
  }, [approvals, risk, alertsPayload, summary, metrics, focusQueueCounts]);

  const rollupLabel = getScopeRollupLabel(user) || 'Company Rollup';
  const headerLocation = locationLabel
    || (locationScope ? locationScope : rollupLabel);
  const periodLabel = formatPeriodLabel(selectedPeriod);
  const shareScopeLabel = useMemo(() => {
    const displayLocation = locationScope || (headerLocation && headerLocation !== rollupLabel ? headerLocation : rollupLabel);
    const capitalized = selectedPeriod
      ? selectedPeriod.charAt(0).toUpperCase() + selectedPeriod.slice(1)
      : '';
    return `${displayLocation} · ${capitalized} Executive Snapshot`.replace(/\s+·\s+Executive/, ' · Executive');
  }, [locationScope, headerLocation, rollupLabel, selectedPeriod]);

  const [shareStatus, setShareStatus] = useState({ kind: '', message: '' });
  const shareTimerRef = useRef(null);
  useEffect(() => () => {
    if (shareTimerRef.current) clearTimeout(shareTimerRef.current);
  }, []);

  const handleCopyLink = useCallback(async () => {
    const url = typeof window !== 'undefined' ? window.location.href : '';
    if (!url) return;

    const showStatus = (kind, message) => {
      setShareStatus({ kind, message });
      if (shareTimerRef.current) clearTimeout(shareTimerRef.current);
      shareTimerRef.current = setTimeout(() => setShareStatus({ kind: '', message: '' }), 3200);
    };

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        showStatus('success', `Link copied — ${shareScopeLabel}`);
        return;
      }
      // Fallback for older browsers / non-secure contexts.
      const input = document.createElement('input');
      input.value = url;
      input.setAttribute('readonly', '');
      input.style.position = 'absolute';
      input.style.left = '-9999px';
      document.body.appendChild(input);
      input.select();
      const ok = document.execCommand && document.execCommand('copy');
      document.body.removeChild(input);
      if (ok) {
        showStatus('success', `Link copied — ${shareScopeLabel}`);
      } else {
        showStatus('error', 'Copy failed. Select the URL bar to share.');
      }
    } catch {
      showStatus('error', 'Copy failed. Select the URL bar to share.');
    }
  }, [shareScopeLabel]);

  const handleExportPdf = useCallback(() => {
    if (isLoading) return;

    const showStatus = (kind, message) => {
      setShareStatus({ kind, message });
      if (shareTimerRef.current) clearTimeout(shareTimerRef.current);
      shareTimerRef.current = setTimeout(() => setShareStatus({ kind: '', message: '' }), 3200);
    };

    const originalTitle = typeof document !== 'undefined' ? document.title : '';
    const filename = buildExportFilename({
      location: locationScope || headerLocation,
      period: selectedPeriod
    });

    let cleaned = false;
    const cleanup = () => {
      if (cleaned) return;
      cleaned = true;
      if (typeof document !== 'undefined') {
        document.title = originalTitle;
        document.body.classList.remove('exec-printing');
      }
      if (typeof window !== 'undefined') {
        window.removeEventListener('afterprint', cleanup);
      }
    };

    if (typeof document !== 'undefined') {
      document.title = filename;
      document.body.classList.add('exec-printing');
    }
    if (typeof window !== 'undefined') {
      window.addEventListener('afterprint', cleanup, { once: true });
    }

    // Fallback in case afterprint never fires (older Safari / dismissed
    // preview): class stays on for at most 5s then self-restores.
    setTimeout(cleanup, 5000);

    try {
      showStatus('info', 'Preparing PDF…');
      if (typeof window !== 'undefined' && typeof window.print === 'function') {
        window.print();
      } else {
        showStatus('error', 'Print not available in this browser.');
        cleanup();
      }
    } catch {
      showStatus('error', 'PDF export failed. Try File → Print instead.');
      cleanup();
    }
  }, [isLoading, locationScope, headerLocation, selectedPeriod]);

  const generatedAt = useMemo(() => new Date().toLocaleString([], {
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit'
  }), [tracker, approvals, attendance]); // eslint-disable-line react-hooks/exhaustive-deps

  // ----- v2 headline metadata -----
  // Relative-time string ("14m ago", "2h ago") for the refresh chip. We use
  // alertsPayload.generatedAt when present (server clock, less drift) and
  // fall back to the same timestamp the rest of the page uses.
  const refreshAgo = useMemo(() => {
    const iso = alertsPayload?.generatedAt;
    if (!iso) return '';
    const then = new Date(iso).getTime();
    if (!Number.isFinite(then)) return '';
    const diffSec = Math.max(0, Math.floor((Date.now() - then) / 1000));
    if (diffSec < 60) return 'just now';
    if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
    if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
    return `${Math.floor(diffSec / 86400)}d ago`;
  }, [alertsPayload?.generatedAt]);

  // Coverage chip: only meaningful past mid-period — early in a month it
  // is normal for not all KPIs to have actuals yet.
  const coverageInfo = useMemo(() => {
    const cov = Number(summary?.coverageRatio);
    if (!Number.isFinite(cov)) return null;
    const pct = Math.round(cov * 100);
    const sampleElapsed = Array.isArray(metrics)
      ? metrics.find(m => Number.isFinite(m?.elapsedPercent))?.elapsedPercent
      : null;
    const isMidPeriod = Number.isFinite(sampleElapsed) && sampleElapsed > 50;
    let tone = 'good';
    if (isMidPeriod && pct < 80) tone = 'watch';
    if (isMidPeriod && pct < 50) tone = 'behind';
    return { pct, tone, isMidPeriod };
  }, [summary?.coverageRatio, metrics]);

  // Period fallback notice: surfaces when trackerService walked back to a
  // prior month because the current one had no data yet.
  const fallbackNotice = useMemo(() => {
    const steps = Number(tracker?.periodFallbackSteps) || 0;
    if (steps <= 0 || !tracker?.period?.periodStart) return null;
    try {
      const d = new Date(`${tracker.period.periodStart}T00:00:00Z`);
      const label = d.toLocaleDateString(undefined, { month: 'long', year: 'numeric', timeZone: 'UTC' });
      return `Showing data from ${label} — current period has no actuals posted yet.`;
    } catch {
      return null;
    }
  }, [tracker?.periodFallbackSteps, tracker?.period?.periodStart]);

  return (
    <WorkspacePage
      className="exec-root exec-page"
      data-testid="workspace-executive"
      data-workspace-id="executive"
      eyebrow="Executive Snapshot"
      title={`${headerLocation} · ${periodLabel}`}
      subtitle="Leadership view of goal attainment, attendance health, and the decisions sitting in your queue — all in one place."
      actions={(
        <div className="exec-header-meta">
            <span className="exec-header-chip">
              <StatusDot tone="good" className="exec-header-chip-dot" aria-hidden />
              Live · updated {generatedAt}
            </span>
            {onNavigate ? (
              <button
                type="button"
                className={`exec-alert-chip ${hasCriticalAlert ? 'is-critical' : ''} ${totalAlertCount === 0 ? 'is-quiet' : ''}`}
                onClick={() => onNavigate('/alerts')}
                aria-label={`Open Alerts Center — ${totalAlertCount} active alert${totalAlertCount === 1 ? '' : 's'}`}
              >
                <StatusDot
                  tone={hasCriticalAlert ? 'behind' : totalAlertCount === 0 ? 'neutral' : 'watch'}
                  className="exec-alert-chip-dot"
                  aria-hidden
                />
                <span className="exec-alert-chip-count">{totalAlertCount}</span>
                <span className="exec-alert-chip-label">
                  {totalAlertCount === 0 ? 'No alerts' : totalAlertCount === 1 ? 'alert' : 'alerts'}
                </span>
              </button>
            ) : null}
            <Button
              size="sm"
              variant="secondary"
              onClick={handleCopyLink}
            >
              Copy link
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={handleExportPdf}
              disabled={isLoading}
            >
              Export PDF
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => refetch()}
              loading={isLoading}
              loadingLabel="Refreshing…"
            >
              Refresh
            </Button>
          </div>
      )}
    >
        {shareStatus.message ? (
          <StatusBanner
            kind={shareStatus.kind || 'success'}
            message={shareStatus.message}
            live
            className="exec-share-toast"
          />
        ) : null}

        {error ? (
          <StatusBanner
            kind="error"
            message={error}
            onRetry={refetch}
            retryLabel="Try again"
          />
        ) : null}

        <section className="exec-section exec-insights" aria-label="Today's story">
          {/* v2 headline meta strip: refresh + coverage + fallback notice. */}
          <div className="exec-meta-strip" role="group" aria-label="Snapshot metadata">
            {refreshAgo ? (
              <span className="exec-meta-chip" title={`Last refreshed ${generatedAt}`}>
                <StatusDot tone="good" className="exec-meta-chip-dot" aria-hidden />
                Refreshed {refreshAgo}
              </span>
            ) : null}
            {coverageInfo ? (
              <span
                className={`exec-meta-chip exec-meta-chip--${coverageInfo.tone}`}
                title={
                  coverageInfo.isMidPeriod
                    ? `${coverageInfo.pct}% of KPIs have actuals posted for this period`
                    : `${coverageInfo.pct}% reported — early in period, gaps are normal`
                }
              >
                <StatusDot tone={coverageInfo.tone} className="exec-meta-chip-dot" aria-hidden />
                {coverageInfo.pct}% coverage
              </span>
            ) : null}
            {focusQueueCounts.total > 0 ? (
              <span
                className={`exec-meta-chip ${focusQueueCounts.overdue > 0 ? 'exec-meta-chip--behind' : ''}`}
                title={`${focusQueueCounts.overdue} overdue · ${focusQueueCounts.total} total`}
              >
                <StatusDot
                  tone={focusQueueCounts.overdue > 0 ? 'behind' : 'watch'}
                  className="exec-meta-chip-dot"
                  aria-hidden
                />
                {focusQueueCounts.overdue > 0
                  ? `${focusQueueCounts.overdue} overdue · ${focusQueueCounts.total} in your queue`
                  : `${focusQueueCounts.total} in your queue`}
              </span>
            ) : null}
          </div>

          {fallbackNotice ? (
            <StatusBanner kind="warning" message={fallbackNotice} className="exec-fallback-notice" />
          ) : null}

          {summary?.narrative ? (
            <p className="exec-narrative" aria-live="polite" aria-atomic="true">{summary.narrative}</p>
          ) : null}

          {/* v2 priorities + recommended actions — replaces the 3-card insights row. */}
          <div className="exec-priorities-row">
            <div className="exec-panel exec-priorities" aria-label="Top priorities">
              <div className="exec-panel-head">
                <h2 className="exec-panel-title">Top priorities</h2>
                <p className="exec-panel-subtitle">Highest-signal alerts to address first.</p>
              </div>
              {topPriorities.length ? (
                <ol className="exec-priority-list">
                  {topPriorities.map((alert, idx) => {
                    const tone = alert.severity === 'critical' ? 'behind'
                      : alert.severity === 'warn' ? 'watch'
                      : 'neutral';
                    const handler = onNavigate && alert.cta?.path
                      ? () => onNavigate(alert.cta.path)
                      : null;
                    return (
                      <li key={alert.id || idx} className="exec-priority-row">
                        <span className="exec-priority-rank" aria-hidden>{idx + 1}</span>
                        <StatusDot tone={tone} className="exec-priority-dot" aria-hidden />
                        <div className="exec-priority-body">
                          <div className="exec-priority-title">{alert.title}</div>
                          {alert.subtitle ? (
                            <div className="exec-priority-sub">{alert.subtitle}</div>
                          ) : null}
                        </div>
                        {handler ? (
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={handler}
                            className="exec-priority-cta"
                          >
                            {alert.cta?.label || 'Open'}
                          </Button>
                        ) : null}
                      </li>
                    );
                  })}
                </ol>
              ) : (
                <EmptyState
                  icon="✓"
                  headline="Nothing critical right now"
                  body="Every location is within tolerance for this period."
                  compact
                />
              )}
            </div>

            <div className="exec-panel exec-actions" aria-label="Recommended actions">
              <div className="exec-panel-head">
                <h2 className="exec-panel-title">Recommended actions</h2>
                <p className="exec-panel-subtitle">Concrete next moves based on the signals above.</p>
              </div>
              <ol className="exec-action-list">
                {recommendedActions.map((action, idx) => {
                  const handler = onNavigate && action.cta?.path
                    ? () => onNavigate(action.cta.path)
                    : null;
                  return (
                    <li key={action.id || idx} className="exec-action-row">
                      <StatusDot tone={action.tone || 'neutral'} className="exec-action-dot" aria-hidden />
                      <div className="exec-action-body">
                        <div className="exec-action-title">{action.title}</div>
                        {action.subtitle ? (
                          <div className="exec-action-sub">{action.subtitle}</div>
                        ) : null}
                      </div>
                      {handler ? (
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={handler}
                          className="exec-action-cta"
                        >
                          {action.cta?.label || 'Open'}
                        </Button>
                      ) : null}
                    </li>
                  );
                })}
              </ol>
            </div>
          </div>
        </section>

        <section className="exec-pulse" aria-label="Executive pulse">
          {/* v2 Phase 3: every pulse tile drills into its detail page. */}
          <PulseTile
            label="Overall attainment"
            value={formatSummaryScore(summary)}
            sub={summary
              ? `${summary.onTrackCount} on track · ${summary.needsAttentionCount} behind`
              : 'Awaiting first goal — set targets to start tracking.'}
            tone={summaryTone(summary)}
            loading={isLoading && !tracker}
            onClick={onNavigate ? () => onNavigate('/location-scorecard') : null}
          />
          <PulseTile
            label="At-work rate"
            value={formatPercent(asPercent(attendance?.atWorkRate))}
            sub={attendance
              ? `Window ${dateRange.startDate} → ${dateRange.endDate}`
              : ''}
            tone={attendanceTone(attendance?.atWorkRate)}
            loading={isLoading && !attendance}
            onClick={onNavigate ? () => onNavigate('/manager-performance') : null}
          />
          <PulseTile
            label="Pending approvals"
            value={formatCount(approvals?.pendingForReview)}
            sub={approvals?.returnedToMe
              ? `${approvals.returnedToMe} returned to you`
              : ''}
            tone={approvalTone(approvals)}
            loading={isLoading && !approvals}
            onClick={onNavigate ? () => onNavigate('/requests-calendar') : null}
          />
          <PulseTile
            label={canSeeRisk ? 'Managers at risk' : 'Managers tracked'}
            value={canSeeRisk ? formatCount(risk.length) : formatCount(summary?.metricCount)}
            sub={canSeeRisk
              ? (risk.length ? 'See the locations flagged below.' : 'Every manager is within attendance policy.')
              : 'KPIs being tracked this period.'}
            tone={canSeeRisk ? (risk.length ? 'danger' : 'good') : 'neutral'}
            loading={isLoading && canSeeRisk && !data.risk}
            onClick={onNavigate ? () => onNavigate('/manager-performance') : null}
          />
        </section>

        <section className="exec-section" aria-label="Scorecard highlights">
          <div className="exec-section-head">
            <div>
              <h2 className="exec-section-title">Scorecard highlights</h2>
              <p className="exec-section-subtitle">
                Six KPIs with the most signal this period — ranked by gap to goal.
              </p>
            </div>
            {onNavigate ? (
              <Button size="sm" variant="secondary" onClick={() => onNavigate('/location-scorecard')}>
                Open full scorecard
              </Button>
            ) : null}
          </div>
          {isLoading && !tracker ? (
            <KpiSkeleton />
          ) : topMetrics.length ? (
            <div className="exec-kpi-grid">
              {topMetrics.map((metric) => (
                <KpiCard
                  key={metric.kpiDefinitionId || metric.kpiKey}
                  variant="modern"
                  label={metric.label}
                  value={formatKpiValue(metric.actual, metric)}
                  goal={formatKpiValue(metric.goal, metric)}
                  status={metric.status}
                  history={buildKpiSparklineHistory(metric)}
                  onClick={onNavigate ? () => onNavigate('/location-scorecard') : null}
                />
              ))}
            </div>
          ) : (
            <EmptyState
              icon="·"
              headline="Awaiting first actuals"
              body="No actuals posted yet for this period. The scorecard will populate as locations report."
            />
          )}
        </section>

        {/*
         * v2 Phase 2: the redundant "Needs attention" alert list is gone —
         * Top priorities at the top of the page already covers the highest-
         * signal items, and the header alert chip routes to /alerts for the
         * full ranked list. What remains here is a collapsed-by-default
         * Wins accordion: nice to celebrate, never the headline. Native
         * <details> gives keyboard + screen-reader behavior without new
         * components.
         */}
        {(() => {
          const winsList = Array.isArray(alertsPayload?.wins) ? alertsPayload.wins : [];
          if (!winsList.length) return null;
          return (
            <section className="exec-section exec-wins" aria-label="What's working">
              <details className="exec-wins-details">
                <summary className="exec-wins-summary">
                  <span className="exec-wins-summary-marker" aria-hidden>▸</span>
                  <span className="exec-wins-summary-label">What's working</span>
                  <span className="exec-wins-summary-count">
                    {winsList.length} streak win{winsList.length === 1 ? '' : 's'} this period
                  </span>
                </summary>
                <ul className="exec-wins-list">
                  {winsList.slice(0, 6).map((win, idx) => {
                    const handler = onNavigate && win.cta?.path
                      ? () => onNavigate(win.cta.path)
                      : null;
                    return (
                      <li key={win.id || idx} className="exec-wins-row">
                        <StatusDot tone="good" className="exec-wins-dot" aria-hidden />
                        <div className="exec-wins-body">
                          <div className="exec-wins-title">{win.title}</div>
                          {win.subtitle ? (
                            <div className="exec-wins-sub">{win.subtitle}</div>
                          ) : null}
                        </div>
                        {handler ? (
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={handler}
                            className="exec-wins-cta"
                          >
                            {win.cta?.label || 'View'}
                          </Button>
                        ) : null}
                      </li>
                    );
                  })}
                </ul>
              </details>
            </section>
          );
        })()}

        <footer className="exec-footer">
          <span>Data source: live operational records · {headerLocation}</span>
          <span>Generated {generatedAt}</span>
        </footer>

        <div className="exec-print-only" aria-hidden>
          <span>
            {headerLocation} · {periodLabel} · Executive Snapshot
          </span>
          <span>
            Generated {generatedAt}
            {typeof window !== 'undefined' && window.location ? ` · ${window.location.href}` : ''}
          </span>
        </div>
    </WorkspacePage>
  );
}

/* ---------- Sub-components ---------- */

export function PulseTile({ label, value, sub, tone = 'neutral', loading, onClick }) {
  const clickable = typeof onClick === 'function';
  const Tag = clickable ? 'button' : 'div';
  const className = [
    'exec-tile',
    `exec-tile--tone-${tone || 'neutral'}`,
    clickable ? 'exec-tile--clickable' : '',
  ].filter(Boolean).join(' ');
  const props = clickable
    ? { type: 'button', onClick, 'aria-label': `${label} — open detail` }
    : {};
  return (
    <Tag className={className} {...props}>
      <div className="exec-tile-label">{label}</div>
      {loading ? (
        <>
          <SkeletonBlock height={32} width="64%" radius="var(--radius-10)" className="exec-tile-skeleton-value" />
          <div className="exec-tile-sub">
            <SkeletonBlock height={18} width="42%" radius="var(--radius-pill)" />
            <SkeletonBlock height={14} width="68%" />
          </div>
        </>
      ) : (
        <>
          <div className="exec-tile-value">{value}</div>
          <div className="exec-tile-sub">
            {tone && tone !== 'neutral' ? (
              <StatusPill tone={tone} className="exec-tile-trend">
                {tone === 'good' ? 'Healthy' : tone === 'warn' ? 'Watch' : 'Action needed'}
              </StatusPill>
            ) : null}
            <span>{sub}</span>
          </div>
        </>
      )}
    </Tag>
  );
}

function KpiSkeleton() {
  return (
    <div className="exec-kpi-grid">
      {Array.from({ length: 6 }).map((_, idx) => (
        <div key={idx} className="kpi-card">
          <SkeletonBlock width="40%" height={10} />
          <SkeletonBlock width="70%" height={14} />
          <SkeletonBlock width="55%" height={24} radius="var(--radius-10)" />
          <SkeletonBlock width="100%" height={6} radius="var(--radius-pill)" />
        </div>
      ))}
    </div>
  );
}

/* ---------- Data helpers ---------- */

function prioritizeMetrics(metrics) {
  // Sort by: behind > on_track > ahead > no_data, then by biggest gap-to-goal first.
  const statusRank = { behind: 0, on_track: 1, ahead: 2, partial: 3, no_data: 4 };
  return [...metrics].sort((a, b) => {
    const ra = statusRank[a.status] ?? 5;
    const rb = statusRank[b.status] ?? 5;
    if (ra !== rb) return ra - rb;
    const pa = Number.isFinite(a.percentToGoal) ? a.percentToGoal : 999;
    const pb = Number.isFinite(b.percentToGoal) ? b.percentToGoal : 999;
    return pa - pb;
  });
}

function buildKpiSparklineHistory(metric = {}) {
  const actual = Number(metric.actual);
  const goal = Number(metric.goal);
  const percent = Number(metric.percentToGoal);

  if (Array.isArray(metric.history)) {
    return metric.history;
  }

  if (Number.isFinite(actual) && Number.isFinite(goal)) {
    const start = actual < goal ? Math.max(0, actual * 0.72) : Math.max(0, goal * 0.9);
    const step = (actual - start) / 11;
    return Array.from({ length: 12 }, (_, index) => start + step * index);
  }

  if (Number.isFinite(percent)) {
    const start = Math.max(0, percent * 0.72);
    const step = (percent - start) / 11;
    return Array.from({ length: 12 }, (_, index) => start + step * index);
  }

  return [];
}

function formatKpiValue(value, metric) {
  if (!Number.isFinite(value)) {
    return '—';
  }
  if (metric.formatType === 'percent') {
    return formatPercent(value);
  }
  return formatMetricValue(value, metric.unit || '', null, metric.formatType || '');
}

function formatSummaryScore(summary) {
  if (!summary || !Number.isFinite(summary.overallScore)) return '—';
  return `${summary.overallScore}%`;
}

function summaryTone(summary) {
  if (!summary || !Number.isFinite(summary.overallScore)) return 'neutral';
  if (summary.overallScore >= 95) return 'good';
  if (summary.overallScore >= 80) return 'warn';
  return 'danger';
}

function attendanceTone(rate) {
  if (!Number.isFinite(rate)) return 'neutral';
  const pct = asPercent(rate);
  if (pct >= 95) return 'good';
  if (pct >= 88) return 'warn';
  return 'danger';
}

function approvalTone(approvals) {
  if (!approvals) return 'neutral';
  const pending = Number(approvals.pendingForReview) || 0;
  const returned = Number(approvals.returnedToMe) || 0;
  if (returned > 0 || pending > 10) return 'danger';
  if (pending > 0) return 'warn';
  return 'good';
}

function asPercent(value) {
  if (!Number.isFinite(value)) return NaN;
  // atWorkRate from the API arrives as a 0-1 fraction; surface it as a percent.
  return value <= 1 ? value * 100 : value;
}

function formatCount(value) {
  if (!Number.isFinite(Number(value))) return '—';
  return new Intl.NumberFormat('en-US').format(Number(value));
}

export function buildExportFilename({ location, period } = {}) {
  const safeLocation = String(location || 'Company Rollup').trim() || 'Company Rollup';
  const safePeriod = formatPeriodLabel(period);
  const raw = `Executive Snapshot - ${safeLocation} - ${safePeriod}`;
  // Strip characters that break file downloads on Windows/macOS.
  return raw.replace(/[\\/:*?"<>|]+/g, '-').replace(/\s+/g, ' ').trim();
}

function formatPeriodLabel(period) {
  if (period === 'monthly') {
    return new Date().toLocaleString([], { month: 'long', year: 'numeric' });
  }
  if (period === 'quarterly') {
    const m = new Date().getMonth();
    const q = Math.floor(m / 3) + 1;
    return `Q${q} ${new Date().getFullYear()}`;
  }
  if (period === 'yearly') {
    return `${new Date().getFullYear()}`;
  }
  return 'This week';
}

export default ExecutiveDashboard;
